import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {ChevronDown, ChevronRight} from 'lucide-react';
import * as reduxSelectors from 'components/store/application.reducers';
import {Button, ButtonType} from 'components/common/Button.component';
import {Drawer} from 'components/common/Drawer.component';
import {ICON_SIZE} from 'components/common/constants';
import {Loader, LoaderSize} from 'components/common/Loader.component';
import {PanelErrorBoundary} from 'components/errorpage/PanelErrorBoundary';
import {taskDetailsPath} from 'components/router/detailsPath';
import AttachmentsPanelComponent from 'components/taskdetails-remake/panels/attachments/AttachmentsPanel.component';
import DocumentDataPanel from 'components/taskdetails-remake/panels/DocumentData.component';
import DocumentEditorPanel from 'components/taskdetails-remake/panels/editor/DocumentEditorPanel.component';
import DraftAndComments from 'components/taskdetails-remake/panels/draftcomments/DraftAndComments.component';
import WorkflowDetailsPanel from 'components/taskdetails-remake/panels/newworkflowdetails/WorkflowDetailsPanel.component';
import {
    getTaskCommentsByUid,
    getTaskDetails,
    handleAddTasksDraftComments,
    integrationIframeHasUnsavedChanges,
    removeSelectedDocument
} from 'components/taskdetails-remake/taskDetails.action';
import {useTranslation} from 'components/translations/useTranslation';
import 'components/mytasks/taskQuickView.css';
import {DETAILS_VIEW_TYPE, DOCUMENT_TYPE, LOADING_STATUS, TASK_ACTIONS} from 'utils/constants';
import {MyTasksActions} from './AvailableActions.class';
import {normalizeTaskAmount} from './taskAmounts.function';
import type {MyTask, Translate} from 'types/myTasks';

// JS or loosely typed panels, so their props are not checked here
const AttachmentsPanel = AttachmentsPanelComponent as React.ComponentType<any>;
const DocumentData = DocumentDataPanel as React.ComponentType<any>;
const DocumentEditor = DocumentEditorPanel as React.ComponentType<any>;

interface TaskQuickViewProps {
    task: MyTask;
    translate: Translate;
    onClose: () => void;
    taskAction: (action: string, keys: string[], options?: {hasDraftComment?: boolean}) => void;
}

// the sections a reader can fold away, remembered across drawers in this browser
const SECTION = Object.freeze({
    DOCUMENT_DATA: 'documentData', WORKFLOW: 'workflow', COMMENTS: 'comments', ATTACHMENTS: 'attachments',
    EDITOR: 'editor'
});
const COLLAPSED_KEY = 'myTasks.quickView.collapsed';

const readCollapsed = (): string[] => {
    try {
        const stored = JSON.parse(window.localStorage.getItem(COLLAPSED_KEY) ?? '[]');
        return Array.isArray(stored) ? stored : [];
    } catch {
        return [];
    }
};

/**
 * Quick view of one task from the new My tasks list, in a drawer beside the list. It is the task
 * details page's own panels, restyled to fit (see taskQuickView.css): the drawer makes no calls of
 * its own - it asks the store for the task's details as the details page does, and each panel
 * loads the rest the way it always does.
 */
const TaskQuickView = ({task, translate, onClose, taskAction}: TaskQuickViewProps) => {

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const {translationKeyExists} = useTranslation();
    const taskDetails = useSelector(reduxSelectors.getDetails);
    const loadingStatus = useSelector(reduxSelectors.getAttachmentsLoadingStatus);
    const attachments = useSelector(reduxSelectors.getTaskAttachments);
    const draftComment = useSelector(reduxSelectors.getTaskDraftCommentText);
    const commentsCount = useSelector(reduxSelectors.getTaskDraftCommentsCount);
    const commentsLoading = useSelector(reduxSelectors.getTaskDraftCommentsLoadingStatus);
    const [collapsed, setCollapsed] = useState<string[]>(readCollapsed);
    const isTaskDetailsLocked = useSelector(reduxSelectors.getIsTaskDetailsLocked);
    const tasksWaitingWSUpdate = useSelector(reduxSelectors.getTasksWaitingWSUpdate);
    const documentDataLoading = useSelector(reduxSelectors.getTaskDocumentDataLoadingStatus);
    const workflowDetailsLoading = useSelector(reduxSelectors.getWorkflowDetailsLoadingStatus);

    // the same load the details page starts with, and the same reset of its picked attachment
    useEffect(() => {
        dispatch(getTaskDetails(task.key) as any);
        dispatch(removeSelectedDocument());
    }, [dispatch, task.key]);

    // the drawer's editor is read-only, so it has no edits to lose; this only keeps a stray flag from
    // outliving the drawer and blocking navigation on the details page
    useEffect(() => () => {
        dispatch(integrationIframeHasUnsavedChanges(false));
    }, [dispatch]);

    // the store holds one task's details: until they are this task's, the panels wait
    const loaded = loadingStatus === LOADING_STATUS.DONE && taskDetails?.key === task.key;
    const failed = loadingStatus === LOADING_STATUS.ERROR;

    const amounts = useMemo(() => normalizeTaskAmount(task, translate), [task, translate]);
    const documentType = translate('documentType.' + DOCUMENT_TYPE.asString(task.documentType));
    // the amount rides along on the title rather than taking a row of its own
    const heading = [
        task.description || task.supplierName || task.displayId || documentType,
        [amounts.amount, amounts.unit].filter(Boolean).join(' ')
    ].filter(Boolean).join(' - ');
    const actions = useMemo(() => new MyTasksActions(task), [task]);

    // held back exactly when the details page holds back its own action buttons (see Subnavbar):
    // while the task is locked, waits on an editor save, or any of its panels is still loading
    const disableActions = Boolean(isTaskDetailsLocked || tasksWaitingWSUpdate?.includes(task.key) ||
        documentDataLoading || workflowDetailsLoading || commentsLoading || loadingStatus === LOADING_STATUS.LOADING);

    // a draft typed in the drawer counts as much as one the list already knew about
    const hasDraftComment = task.hasDraftComment || Boolean(draftComment);
    // the action opens a dialog of its own, at the drawer's own layer, so the drawer steps aside
    const act = (action: string, options?: {hasDraftComment?: boolean}) => {
        onClose();
        taskAction(action, [task.key], options);
    };

    const toggleSection = (id: string) => {
        const next = collapsed.includes(id) ? collapsed.filter(each => each !== id) : [...collapsed, id];
        setCollapsed(next);
        try {
            window.localStorage.setItem(COLLAPSED_KEY, JSON.stringify(next));
        } catch {
            // remembering it is a convenience only
        }
    };

    /**
     * A collapsible section of the drawer. Folding one only hides it: the panel inside keeps its
     * state and requests - a draft comment mid-save, an edit in the editor - for when it opens.
     */
    const section = (id: string, title: string, count: number | null, body: React.ReactNode,
                     options: {className?: string; dataCy?: string} = {}) => {
        const open = !collapsed.includes(id);
        const bodyId = 'quick-view-section-' + id;
        return (
            <section className={'task-quick-view__section' + (options.className ? ' ' + options.className : '')}
                     data-cy={options.dataCy}>
                <h3 className="task-quick-view__section-title">
                    <button type="button"
                            className="task-quick-view__section-toggle"
                            aria-expanded={open}
                            aria-controls={bodyId}
                            onClick={() => toggleSection(id)}
                            data-cy={'myTasks.quickView.toggle.' + id}>
                        {open ? <ChevronDown size={ICON_SIZE.SMALL} aria-hidden="true"/> :
                            <ChevronRight size={ICON_SIZE.SMALL} aria-hidden="true"/>}
                        {title}
                        {count !== null && <span className="ga-badge ga-badge--text ga-badge--muted">{count}</span>}
                    </button>
                </h3>
                <div id={bodyId} className="task-quick-view__section-body" hidden={!open}>
                    {body}
                </div>
            </section>
        );
    };

    const allowExtraComments = Boolean(taskDetails?.taskPermission?.allowExtraComments);
    // as the comments panel counts them: the loaded list where extra comments are allowed, the
    // task's own count where they are not
    const shownCommentsCount = allowExtraComments
        ? (commentsLoading ? 0 : commentsCount ?? 0)
        : task.numberOfComments ?? 0;

    // the editor's title as its own panel words it: the integration's name, translated where it can be
    const applicationName = task.externalApplicationName || task.applicationTypeName || '';
    const applicationKey = 'taskDetail.applicationName.' + applicationName.replace(/[ .]/g, '');
    const editorTitle = translate('taskDetail.externalEditor') + ' ' +
        (translationKeyExists(applicationKey) ? translate(applicationKey) : applicationName);

    const footer = (
        <>
            {actions.isEnabled(TASK_ACTIONS.REVIEW) ?
                <Button
                    type={ButtonType.PRIMARY}
                    onClick={() => act(TASK_ACTIONS.REVIEW)}
                    disabled={disableActions}
                    dataCy="myTasks.quickView.review"
                    text={translate('myTasks.actionBar.review')}
                /> :
                <>
                    {task.taskActions?.includes('quick-approve') &&
                        <Button
                            type={ButtonType.PRIMARY}
                            onClick={() => act(TASK_ACTIONS.APPROVE, {hasDraftComment})}
                            disabled={disableActions}
                            dataCy="myTasks.quickView.approve"
                            text={translate('myTasks.task.approve')}
                        />
                    }
                    {task.taskActions?.includes('quick-reject') &&
                        <Button
                            type={ButtonType.SECONDARY}
                            onClick={() => act(TASK_ACTIONS.REJECT)}
                            disabled={disableActions}
                            dataCy="myTasks.quickView.reject"
                            text={translate('myTasks.task.reject')}
                        />
                    }
                </>
            }
            <Button
                type={ButtonType.GHOST}
                onClick={() => navigate(taskDetailsPath(task))}
                dataCy="myTasks.quickView.openDetails"
                text={translate('myTasks.overview.quickView.openFullDetails')}
            />
        </>
    );

    return (
        <Drawer
            open={true}
            onClose={onClose}
            label={documentType}
            title={heading}
            closeAriaLabel={translate('myTasks.overview.quickView.close')}
            resizeAriaLabel={translate('myTasks.overview.quickView.resize')}
            dataCy="myTasks.quickView"
            footer={footer}
        >
            {/* the size container the panels below grow with as the drawer is widened */}
            <div className="task-quick-view">
                {failed &&
                    <p className="task-quick-view__empty">{translate('myTasks.overview.quickView.loadFailed')}</p>
                }

                {!failed && !loaded && <Loader size={LoaderSize.Medium} dataCy="myTasks.quickView.loading"/>}

                {loaded &&
                    <>
                        {section(SECTION.DOCUMENT_DATA, translate('taskDetail.details', documentType), null,
                            <PanelErrorBoundary panel="documentData">
                                <DocumentData taskId={task.key}
                                              documentType={task.documentType}
                                              translate={translate}
                                              readOnly={true}
                                              isDocument={false}/>
                            </PanelErrorBoundary>,
                            {className: 'task-quick-view__document-data', dataCy: 'myTasks.quickView.documentData'}
                        )}

                        {section(SECTION.WORKFLOW, translate('taskDetail.workflowDetails'), null,
                            <PanelErrorBoundary panel="workflowDetails">
                                <WorkflowDetailsPanel taskId={task.key}
                                                      isDocument={false}
                                                      taskDetails={taskDetails}
                                                      documentDueDate={task.documentDueDate}
                                                      taskDueDate={task.dueDate}/>
                            </PanelErrorBoundary>,
                            {className: 'task-quick-view__workflow', dataCy: 'myTasks.quickView.workflow'}
                        )}

                        {section(SECTION.COMMENTS, translate('taskDetail.comments'), shownCommentsCount,
                            // wired up as the details page wires it
                            <PanelErrorBoundary panel="comments">
                                <DraftAndComments taskId={task.key}
                                                  uid={taskDetails.uid}
                                                  documentUid={task.documentUid ?? task.uidDocument}
                                                  readOnly={Boolean(task.isHandled)}
                                                  initialCommentsCount={task.numberOfComments ?? 0}
                                                  addCommentHandler={(taskKey: string, comment: string, documentUid: string, uid: string) =>
                                                      dispatch(handleAddTasksDraftComments(taskKey, comment, documentUid, uid) as any)}
                                                  getCommentsHandler={(uid: string) => dispatch(getTaskCommentsByUid(uid) as any)}
                                                  allowExtraComments={allowExtraComments}/>
                            </PanelErrorBoundary>,
                            {className: 'task-quick-view__comments', dataCy: 'myTasks.quickView.comments'}
                        )}

                        {section(SECTION.ATTACHMENTS, translate('myTasks.overview.quickView.attachments'),
                            attachments?.length ?? 0,
                            <PanelErrorBoundary panel="attachments">
                                <AttachmentsPanel attachments={attachments}
                                                  loadingStatus={loadingStatus}
                                                  taskDetails={taskDetails}
                                                  translate={translate}
                                                  type={DETAILS_VIEW_TYPE.TASK}/>
                            </PanelErrorBoundary>,
                            {className: 'task-quick-view__attachments', dataCy: 'myTasks.quickView.attachments'}
                        )}

                        {taskDetails.externalEditorAvailable &&
                            section(SECTION.EDITOR, editorTitle, null,
                                <PanelErrorBoundary panel="documentEditor">
                                    <DocumentEditor params={{taskId: task.key}}
                                                    task={task}
                                                    taskDetails={taskDetails}
                                                    type={DETAILS_VIEW_TYPE.TASK}
                                                    isHistory={false}
                                                    // the drawer has no save/discard prompt on close, so editing stays on the details page
                                                    editorReadOnly={true}
                                                    renderInPortal={false}
                                                    inDetachedWindow={true}
                                                    translate={translate}
                                                    translationKeyExists={translationKeyExists}/>
                                </PanelErrorBoundary>,
                                {className: 'task-quick-view__editor', dataCy: 'myTasks.quickView.editor'}
                            )
                        }
                    </>
                }
            </div>
        </Drawer>
    );
};

export default TaskQuickView;
